import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import { Article } from "../../entities/Article";
import { ActivatedRoute } from "@angular/router";
import { NavController } from "@ionic/angular";
import { API_URL } from "../../../environments/environment";
import { AuthService } from "../../services/auth/auth.service";
import { NewsService } from "../../services/news/news.service";
import { AlertService } from "../../services/alert/alert.service";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { HelpersModule } from "../../modules/helpers/helpers.module";
import { BrowserService } from "../../services/browser/browser.service";

@Component({
    selector: "app-news-detail",
    templateUrl: "./news-detail.page.html",
    styleUrls: ["./news-detail.page.scss"]
})
export class NewsDetailPage implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild("wysiwygContainer", { static: false })
    wysiwygContainer: ElementRef;
    private placeholderImageUrl = "../assets/images/default-account.png";
    public article = new Article(null);
    public isDataLoaded = false;
    public articles = [];
    public author = null;
    public coloredToolbar = false;
    public toolbarOpacity = "";
    private readonly articleId;
    private readonly previewLang = "";
    public attachment = null;
    private wysiwygClickHandler: any;
    public language =
        this.authService.authState.getValue().authUser?.language_code ?? "en";

    constructor(
        private newsService: NewsService,
        private route: ActivatedRoute,
        private navController: NavController,
        private authService: AuthService,
        private alertService: AlertService,
        private translateService: TranslateService,
        private browserService: BrowserService,
        private cdr: ChangeDetectorRef
    ) {
        if (this.route.snapshot.queryParams.lang) {
            this.previewLang = this.route.snapshot.queryParams.lang;
        }

        this.articleId = parseInt(this.route.snapshot.params.articleId, 0);
    }

    ngOnInit() {
        this.newsService
            .fetchArticle(this.articleId, this.previewLang)
            .subscribe(
                (data) => {
                    this.article = new Article(data);
                    this.articles = (data["relatedArticles"] ?? []).map(
                        (ra) => new Article(ra)
                    );
                    this.author = this.article.profile;
                    this.isDataLoaded = true;

                    // Trigger change detection to ensure DOM is updated
                    this.cdr.detectChanges();

                    // If view is already initialized, attach event listeners
                    if (this.wysiwygContainer) {
                        this.attachWysiwygEventListeners();
                    }
                },
                (err) => {
                    this.navController.navigateRoot(["/news"]);
                    this.alertService.warning(
                        this.translateService.instant("news-detail.no-access")
                    );
                }
            );
    }

    ngAfterViewInit() {
        // This runs after the view is initialized
        // If data is already loaded, attach the event listener
        if (this.isDataLoaded && this.wysiwygContainer) {
            this.attachWysiwygEventListeners();
        }
    }

    ngOnDestroy() {
        if (this.wysiwygContainer && this.wysiwygClickHandler) {
            this.wysiwygContainer.nativeElement.removeEventListener(
                "click",
                this.wysiwygClickHandler
            );
        }
    }

    goBack() {
        this.navController.pop();
    }

    onScroll($event) {
        if ($event && $event.detail && $event.detail.scrollTop) {
            const scrollTop = $event.detail.scrollTop;
            this.toolbarOpacity = `hsla(0, 0%, 100%, ${scrollTop / 255})`;
            this.coloredToolbar = scrollTop >= 225;
        } else {
            this.toolbarOpacity = `hsla(0, 0%, 100%, 0)`;
            this.coloredToolbar = false;
        }
    }

    authorDetails(author) {
        this.navController.navigateForward([`/authors/${author.id}`]);
    }

    newsDetail(article: Article) {
        this.navController.navigateForward([`/news/${article.id}`]);
    }

    loadSecureImage(article) {
        const token = this.authService.authState.getValue().token;

        return article.getFeaturedImagePath() + `?token=${token}`;
    }

    fetchImageFromAuthState() {
        const token = this.authService.authState.getValue().token;

        if (this.author && this.author.photo) {
            return `${API_URL}/files/contacts/${this.author.photo}?token=${token}`;
        } else {
            return this.placeholderImageUrl;
        }
    }

    loadArticleSecureImage(article) {
        const token = this.authService.authState.getValue().token;

        return article.getFeaturedImagePath() + `?token=${token}`;
    }

    get articleComponents() {
        return this.article.components.filter((c: any) => {
            return c.component_type_id !== 5;
        });
    }

    get pdfComponents() {
        return this.article.components.filter((c: any) => {
            return c.component_type_id === 5;
        });
    }

    getTranslationIdForCurrentLanguage(component: any): number {
        if (
            !component ||
            !component.attributes ||
            !component.attributes.translations
        ) {
            // Fallback to component ID if no translations available
            return component?.attributes?.id || component?.id;
        }

        const translations = component.attributes.translations;
        const currentLangTranslation = translations.find(
            (t: any) => t.language === this.language
        );

        // If translation found for current language, use its ID
        if (currentLangTranslation) {
            return currentLangTranslation.id;
        }

        // If no translation found for current language, try to find first non-empty translation
        const nonEmptyTranslation = translations.find(
            (t: any) => t.content && t.content.trim() !== ""
        );

        if (nonEmptyTranslation) {
            return nonEmptyTranslation.id;
        }

        // Final fallback to component ID
        return component?.attributes?.id || component?.id;
    }

    private attachWysiwygEventListeners() {
        // Only attach if not already attached
        if (this.wysiwygClickHandler) {
            return;
        }

        // Only listen for links in the wysiwyg container else it will open the BoerenBussiness app on every click throughout the app.
        this.wysiwygClickHandler = (e: Event) => {
            e.preventDefault();

            const el = e.target as HTMLAnchorElement;
            const dataset = el.dataset;

            if (dataset.redactorType === "articlelink" && dataset.link) {
                this.navController.navigateForward([`/news/${dataset.link}`]);
            }

            if (dataset.articleChapterLink && dataset.manualId) {
                this.navController.navigateForward(
                    [`/manuals/${dataset.manualId}`],
                    { queryParams: { scrollTo: dataset.articleChapterLink } }
                );
            }

            if (typeof el.href !== "undefined") {
                this.browserService.openLink(el.href);
            }
        };

        if (this.wysiwygContainer && this.wysiwygContainer.nativeElement) {
            this.wysiwygContainer.nativeElement.addEventListener(
                "click",
                this.wysiwygClickHandler
            );
        }
    }

    navigateToPdf(id: number) {
        this.navController.navigateForward([`/preview/3/${id}`]);
    }

    get getPublishDate() {
        return moment(this.article.published_at).format("D MMMM YYYY");
    }
}
